/**
 * Copyright 2023, IntraLinks, Inc. All rights reserved.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Redirect, Route, Switch, useLocation
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IlDropdown, IlLoading } from 'il-framework-component';
import ROUTES from '../../app/shared/utils/router.util';
import SideNavigation from '../../app/shared/components/SideNavigation/SideNavigation';
import { I18nNamespaces, FactorStatuses } from '../../app/shared/utils/constants.util';
import PersonalInfo from './Info/PersonalInfo';
import { setIsLoading } from '../../app/shared/AppSlice/AppSlice';
import { getApplications, getProfile } from './profileSlice';
import { RootState, routerHistory } from '../../app/store';
import UpdatePassword from './UpdatePassword/updatePassword';
import EnrollMFASelect from './EnrollMFA/EnrollMFASelect';
import { getSupportedFactors } from './EnrollMFA/enrollSlice';
import ExternalApplications from './ExternalAppsAccess/externalAppsAccess';

import styles from './ProfileHome.scss';
import EmailPreferences from './EmailPreferences/EmailPreferences';
import { loadDefaultPreferences } from './EmailPreferences/EmailPreferencesSlice';
import { IProfileApps } from '../../domain/models/IProfileApps';

import LoAFactors from './LoAFactors/LoAFactors';
import { loadSecuritySetupPage } from '../SecuritySetup/setupSecuritySlice';

interface ISideBarContent {
  label: string;
  route: string;
}

const ProfileHomeContainer = (): JSX.Element => {
  const { t } = useTranslation([I18nNamespaces.PROFILE, I18nNamespaces.LOA_ENRICHMENT]);
  const dispatch = useDispatch();
  const location = useLocation();

  const { ssoEnabled } = useSelector((state: RootState) => state.profile.profileDetails);
  const entitledApplications = useSelector((state: RootState) => state.profile.applications);
  const isAppLoading = useSelector((state: RootState) => state.appState.isAppLoading);
  const { defaultPreferencesMap } = useSelector((state: RootState) => state.emailPreferencesSettings);
  const [selectedItemIndex, setSelectedItemIndex] = useState<number>(0);
  const [mobileDropdownList, setMobileDropdownList] = useState<ISideBarContent[]>([]);
  const enableEmailPreferences = entitledApplications && entitledApplications
    .some((app: IProfileApps) => defaultPreferencesMap.has(app.id));
  const profileLoaded = useSelector((s: RootState) => s.profile.profileDetails?.id !== '');
  const loaFactors = useSelector((s: RootState) => s.setupSecurity.enrolledFactors);
  const factorsLoaded = useSelector((s: RootState) => s.setupSecurity.factorsLoaded);
  const hasLoaFactors = (loaFactors?.filter((f) => f.status === FactorStatuses.ACTIVE).length ?? 0) > 0;
  const dataReady = profileLoaded && (!ssoEnabled || factorsLoaded);

  useEffect(() => {
    (async (): Promise<void> => {
      await dispatch(setIsLoading(true));
      await Promise.all([
        dispatch(getProfile()),
        dispatch(getApplications()),
        dispatch(loadDefaultPreferences())
      ]);
      await dispatch(setIsLoading(false));
    })();
  }, []);

  useEffect(() => {
    (async (): Promise<void> => {
      if (!profileLoaded) return;
      if (ssoEnabled) return;
      await dispatch(setIsLoading(true));
      await dispatch(getSupportedFactors());
      await dispatch(setIsLoading(false));
    })();
  }, [ssoEnabled, profileLoaded]);

  useEffect(() => {
    if (!profileLoaded) return;
    if (!ssoEnabled) return;
    dispatch(loadSecuritySetupPage(t));
  }, [ssoEnabled, profileLoaded]);

  const navList: ISideBarContent[] = useMemo(() => {
    if (!dataReady) return [];

    const list: ISideBarContent[] = [{
      label: t('profile.profileInfo'),
      route: ROUTES.PROFILE_CONTAINER
    }];

    if (!ssoEnabled) {
      list.push(
        {
          label: t('profile.accountRecovery'),
          route: ROUTES.RECOVERY_SETTINGS_CONTAINER
        },
        {
          label: t('profile.updatePassword'),
          route: ROUTES.UPDATE_PASSWORD_CONTAINER
        }
      );
    }

    if (ssoEnabled && hasLoaFactors) {
      list.push({
        label: t('loaEnrichment.profile.title', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        route: ROUTES.LOA_FACTORS_CONTAINER
      });
    }

    if (enableEmailPreferences) {
      list.push({
        label: t('profile.emailPreferences'),
        route: ROUTES.EMAIL_PREFERENCES
      });
    }

    return list;
  }, [t, dataReady, ssoEnabled, hasLoaFactors, enableEmailPreferences]);

  useEffect(() => {
    setMobileDropdownList(navList);

    const idx = navList.findIndex((item) => item.route === location.pathname);
    setSelectedItemIndex(idx === -1 ? 0 : idx);
  }, [navList, location.pathname]);

  document.title = t('Account Profile', { ns: I18nNamespaces.PROFILE });

  const dropdownValue = (e: any): void => {
    routerHistory.push(e.selectedItem.route);
    const selectId = mobileDropdownList.findIndex((list) => list.route === e.selectedItem.route);
    setSelectedItemIndex(selectId);
  };

  return (
    <div className={styles.profileAppContainer}>
      {(isAppLoading || !dataReady) && <IlLoading withOverlay />}

      <div className='bx--row'>
        <div className='bx--col-lg-3 bx--col-xlg-3 bx--col-md-3 bx--col-sm-0'>
          <SideNavigation
            navItems={navList}
          />
        </div>
        <div className='bx--col-lg-0 bx--col-xlg-0 bx--col-md-0  bx--col-sm-4'>
          <div className={styles.profileDropDown}>
            <IlDropdown
              initialSelectedItem={mobileDropdownList[selectedItemIndex]}
              id='profileDropdown'
              items={mobileDropdownList}
              label='Select Profile Dropdown'
              selectedItem={mobileDropdownList[selectedItemIndex]}
              itemToString={(item: ISideBarContent): string => (item ? t(item.label) : '')}
              onChange={(e): void => dropdownValue(e)}
            />

          </div>
        </div>
        <div className='bx--col-lg-13 bx--col-xlg-13 bx--col-md-13 bx--col-sm-4'>
          {dataReady ? (
            <Switch>
              <Route exact path={ROUTES.PROFILE_CONTAINER} component={PersonalInfo} />
              {!ssoEnabled && (
                <Route exact path={ROUTES.RECOVERY_SETTINGS_CONTAINER} component={EnrollMFASelect} />
              )}
              {!ssoEnabled && (
                <Route exact path={ROUTES.UPDATE_PASSWORD_CONTAINER} component={UpdatePassword} />
              )}
              {enableEmailPreferences && (
                <Route exact path={ROUTES.EMAIL_PREFERENCES} component={EmailPreferences} />
              )}

              {ssoEnabled && hasLoaFactors ? (
                <Route exact path={ROUTES.LOA_FACTORS_CONTAINER} component={LoAFactors} />
              ) : (
                <Route
                  exact
                  path={ROUTES.LOA_FACTORS_CONTAINER}
                  render={() => <Redirect to={ROUTES.PROFILE_CONTAINER} />}
                />
              )}

              <Route exact path={ROUTES.EXTERNAL_APP_ACCESS_CONTAINER} component={ExternalApplications} />
            </Switch>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ProfileHomeContainer;
